import {
  ApiError,
  type Customer,
  type CustomerLink,
  createCustomerLinkSchema,
  createCustomerSchema,
  formatUsPhone,
  queryKeys,
} from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Plus, Trash2, Users } from 'lucide-react'
import { useState } from 'react'
import { api } from '../lib/api.js'

type PickedCustomer = Pick<Customer, 'id' | 'name' | 'businessName' | 'phone' | 'email'>

const subtitle = (c: Partial<Pick<PickedCustomer, 'businessName' | 'phone'>>) =>
  [c.businessName, c.phone ? formatUsPhone(c.phone) : null].filter(Boolean).join(' · ')

/**
 * Family/payer links on a customer's profile (t1117). A link moves nothing —
 * it lets staff offer the other customer's saved card when taking a payment
 * for this one, so the note recorded here is the consent record for that.
 * The same link shows on both profiles.
 */
export function LinkedCustomersCard({
  customer,
  canWrite,
}: {
  customer: Customer
  canWrite: boolean
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<CustomerLink | null>(null)

  const { data: links } = useQuery({
    queryKey: queryKeys.customers.links(customer.id),
    queryFn: () => api.get<CustomerLink[]>(`/customers/${customer.id}/links`),
  })

  // The other profile shows this link too, and which cards the counter offers
  // for either customer changes with it.
  const refreshAfterChange = async (otherId?: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.links(customer.id) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.paymentMethods(customer.id) }),
      ...(otherId
        ? [
            queryClient.invalidateQueries({ queryKey: queryKeys.customers.links(otherId) }),
            queryClient.invalidateQueries({
              queryKey: queryKeys.customers.paymentMethods(otherId),
            }),
          ]
        : []),
    ])
  }

  const remove = useMutation({
    mutationFn: (link: CustomerLink) => api.delete(`/customers/${customer.id}/links/${link.id}`),
    onSuccess: async (_data, link) => {
      await refreshAfterChange(link.customer.id)
      toast.success('Link removed')
      setRemoving(null)
    },
    onError: (error: Error) => toast.error(error.message),
  })

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Users className="size-4" aria-hidden="true" /> Linked customers
        </CardTitle>
        {canWrite ? (
          <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
            <Plus className="size-3.5" aria-hidden="true" /> Link
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="p-0">
        {links && links.length > 0 ? (
          <ul className="divide-y divide-border">
            {links.map((link) => (
              <li key={link.id} className="flex items-start gap-2 px-4 py-2.5 text-sm">
                <div className="min-w-0 flex-1">
                  <Link
                    to="/customers/$customerId"
                    params={{ customerId: link.customer.id }}
                    className="font-medium hover:underline"
                  >
                    {link.customer.name}
                  </Link>
                  {subtitle(link.customer) ? (
                    <p className="text-2xs text-foreground-muted">{subtitle(link.customer)}</p>
                  ) : null}
                  {link.note ? (
                    <p className="mt-1 text-2xs text-foreground-muted">{link.note}</p>
                  ) : null}
                  {link.createdByName ? (
                    <p className="text-2xs text-foreground-muted">
                      Recorded by {link.createdByName} on{' '}
                      {new Date(link.createdAt).toLocaleDateString()}
                    </p>
                  ) : null}
                </div>
                {link.label ? <Badge tone="neutral">{link.label}</Badge> : null}
                {canWrite ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Unlink ${link.customer.name}`}
                    onClick={() => setRemoving(link)}
                  >
                    <Trash2 className="size-3.5" aria-hidden="true" />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-4 py-3 text-sm text-foreground-muted">
            Not linked to anyone. Link a family member so staff can use their saved card when taking
            a payment here — nothing moves between the two files.
          </p>
        )}
      </CardContent>

      <AddLinkDialog
        customer={customer}
        open={adding}
        onOpenChange={setAdding}
        onLinked={(other) => refreshAfterChange(other.id)}
      />

      <Dialog open={!!removing} onOpenChange={(open) => !open && setRemoving(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Unlink {removing?.customer.name}?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-muted">
              Staff will no longer be offered each other's saved cards at the counter. Past payments
              keep showing whose card was used, and no cards, orders, or rentals move.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => removing && remove.mutate(removing)}
              disabled={remove.isPending}
            >
              {remove.isPending ? 'Removing…' : 'Unlink'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

function AddLinkDialog({
  customer,
  open,
  onOpenChange,
  onLinked,
}: {
  customer: Customer
  open: boolean
  onOpenChange: (open: boolean) => void
  onLinked: (other: PickedCustomer) => Promise<unknown>
}) {
  const toast = useToast()
  const [search, setSearch] = useState('')
  const [picked, setPicked] = useState<PickedCustomer | null>(null)
  const [label, setLabel] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | undefined>()
  const [creating, setCreating] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createPhone, setCreatePhone] = useState('')
  const [createEmail, setCreateEmail] = useState('')
  const [createError, setCreateError] = useState<string | undefined>()
  const [conflict, setConflict] = useState<{ id: string; name: string } | null>(null)

  const reset = () => {
    setSearch('')
    setPicked(null)
    setLabel('')
    setNote('')
    setError(undefined)
    setCreating(false)
    setCreateName('')
    setCreatePhone('')
    setCreateEmail('')
    setCreateError(undefined)
    setConflict(null)
  }

  // Seed the form from whatever was typed in the search box.
  const openCreate = () => {
    const term = search.trim()
    const isEmail = term.includes('@')
    const isPhone = !isEmail && /^[\d\s()+.-]+$/.test(term) && /\d/.test(term)
    setCreateName(term && !isEmail && !isPhone ? term : '')
    setCreatePhone(isPhone ? term : '')
    setCreateEmail(isEmail ? term : '')
    setCreateError(undefined)
    setConflict(null)
    setCreating(true)
  }

  const queryClient = useQueryClient()
  const create = useMutation({
    mutationFn: async (): Promise<PickedCustomer | null> => {
      const parsed = createCustomerSchema.safeParse({
        phone: createPhone.trim() || null,
        name: createName,
        email: createEmail.trim() || null,
      })
      if (!parsed.success) {
        throw new Error(parsed.error.issues[0]?.message ?? 'Check the form and try again')
      }
      try {
        return await api.post<PickedCustomer>('/customers', parsed.data)
      } catch (err) {
        if (!(err instanceof ApiError) || err.status !== 409) throw err
        const details = err.details as { customerId?: string; customerName?: string } | undefined
        if (details?.customerId) {
          setConflict({ id: details.customerId, name: details.customerName ?? 'another customer' })
        } else {
          setCreateError(err.message)
        }
        return null
      }
    },
    onSuccess: async (created) => {
      if (!created) return
      await queryClient.invalidateQueries({ queryKey: queryKeys.customers.all() })
      toast.success('Customer created')
      setPicked(created)
      setCreating(false)
      setSearch('')
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const { data, isFetching } = useQuery({
    queryKey: queryKeys.customers.list({ q: search }),
    queryFn: () =>
      api.get<{ items: PickedCustomer[] }>('/customers', { query: { q: search, pageSize: 6 } }),
    enabled: open && !picked && search.trim().length >= 2,
  })
  const results = (data?.items ?? []).filter((c) => c.id !== customer.id)

  const link = useMutation({
    mutationFn: () => {
      if (!picked) throw new Error('Pick the customer to link')
      const parsed = createCustomerLinkSchema.safeParse({
        customerId: picked.id,
        label: label.trim() || null,
        note: note.trim() || null,
      })
      if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? 'Check the details')
      return api.post<CustomerLink>(`/customers/${customer.id}/links`, parsed.data)
    },
    onSuccess: async () => {
      if (picked) await onLinked(picked)
      toast.success('Customers linked')
      onOpenChange(false)
      reset()
    },
    onError: (err: Error) => {
      if (err instanceof ApiError && err.status === 409) setError(err.message)
      else toast.error(err.message)
    },
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Link a customer to {customer.name}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-foreground-muted">
            They stay separate customers. Linking only lets staff choose one's saved card when
            taking a payment for the other.
          </p>

          <div className="space-y-2 rounded-lg border border-border p-3">
            <p className="text-xs font-medium text-foreground-muted">Customer to link</p>
            {picked ? (
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm">
                  <p className="font-medium">{picked.name}</p>
                  <p className="text-xs text-foreground-muted">{subtitle(picked)}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setPicked(null)}>
                  Change
                </Button>
              </div>
            ) : creating ? (
              <div className="space-y-3">
                {conflict ? (
                  <Alert tone="warning">
                    <div className="space-y-2">
                      <p>
                        {conflict.id === customer.id
                          ? 'That is this same customer.'
                          : `That phone number or email already belongs to ${conflict.name}.`}
                      </p>
                      {conflict.id !== customer.id ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setPicked({ ...conflict, businessName: null, phone: null, email: null })
                            setCreating(false)
                            setConflict(null)
                          }}
                        >
                          Link {conflict.name} instead
                        </Button>
                      ) : null}
                    </div>
                  </Alert>
                ) : null}
                <Field label="Name" required>
                  {(props) => (
                    <Input
                      {...props}
                      autoFocus
                      value={createName}
                      onChange={(e) => setCreateName(e.target.value)}
                      disabled={create.isPending}
                    />
                  )}
                </Field>
                <Field label="Phone">
                  {(props) => (
                    <Input
                      {...props}
                      type="tel"
                      value={createPhone}
                      onChange={(e) => setCreatePhone(e.target.value)}
                      disabled={create.isPending}
                    />
                  )}
                </Field>
                <Field label="Email" error={createError}>
                  {(props) => (
                    <Input
                      {...props}
                      type="email"
                      value={createEmail}
                      onChange={(e) => {
                        setCreateEmail(e.target.value)
                        setCreateError(undefined)
                      }}
                      disabled={create.isPending}
                    />
                  )}
                </Field>
                <div className="flex justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={create.isPending}
                    onClick={() => {
                      setCreating(false)
                      setConflict(null)
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    loading={create.isPending}
                    disabled={!createName.trim()}
                    onClick={() => create.mutate()}
                  >
                    Create customer
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Input
                  autoFocus
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value)
                    setError(undefined)
                  }}
                  placeholder="Name, email, or phone…"
                  aria-label="Customer to link"
                />
                {results.length ? (
                  <ul className="divide-y divide-border">
                    {results.map((row) => (
                      <li key={row.id}>
                        <button
                          type="button"
                          className="w-full px-1 py-2 text-start text-sm hover:bg-surface-sunken"
                          onClick={() => {
                            setPicked(row)
                            setSearch('')
                          }}
                        >
                          <span className="block font-medium">{row.name}</span>
                          <span className="block text-2xs text-foreground-muted">
                            {subtitle(row) || '—'}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : data && !isFetching && search.trim().length >= 2 ? (
                  <p className="text-sm text-foreground-muted">No customers match.</p>
                ) : null}
                <Button variant="secondary" size="sm" onClick={openCreate}>
                  Create new customer
                </Button>
              </>
            )}
            {error ? <p className="text-sm text-danger">{error}</p> : null}
          </div>

          <Field label="Relationship" hint="e.g. Parent, Spouse, Family">
            {(props) => (
              <Input {...props} value={label} onChange={(e) => setLabel(e.target.value)} />
            )}
          </Field>
          <Field
            label="Permission note"
            hint="Who agreed that the other person's card may be used, and how"
          >
            {(props) => (
              <Textarea
                {...props}
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => link.mutate()}
            disabled={!picked || creating || link.isPending}
          >
            {link.isPending ? 'Linking…' : 'Link'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
